import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import { getDeals, getContacts, getActivities, getDashboardMetrics } from '../services/api';

/* ── accent palette (mirrors Dashboard) ──────────────────────────── */
const accents: Record<string, { bg: string; border: string; text: string }> = {
  sage:   { bg: 'rgba(144,185,171,0.08)', border: 'rgba(144,185,171,0.25)', text: '#90b9ab' },
  blue:   { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.25)',  text: '#3b82f6' },
  amber:  { bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.25)',  text: '#fbbf24' },
  purple: { bg: 'rgba(168,85,247,0.08)',  border: 'rgba(168,85,247,0.25)',  text: '#a855f7' },
};

/* ── stage colors ────────────────────────────────────────────────── */
const stageColors: Record<string, string> = {
  discovery:     '#3b82f6',
  qualification: '#8b5cf6',
  proposal:      '#fbbf24',
  negotiation:   '#f97316',
  closed_won:    '#22c55e',
  closed_lost:   '#ef4444',
};

/* ── chart tooltip style ─────────────────────────────────────────── */
const tooltipStyle = {
  contentStyle: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-primary)',
    borderRadius: 8,
    color: 'var(--text-primary)',
    fontSize: 12,
  },
};

/* ── component ───────────────────────────────────────────────────── */
export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);

  // raw data
  const [deals, setDeals] = useState<any[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // try analytics endpoint silently
        getDashboardMetrics().catch(() => {});

        const [dealsRes, contactsRes, activitiesRes] = await Promise.allSettled([
          getDeals({ limit: '200' }),
          getContacts({ limit: '1' }),
          getActivities({ limit: '200' }),
        ]);

        const d = dealsRes.status === 'fulfilled'
          ? (dealsRes.value?.items ?? dealsRes.value ?? [])
          : [];
        setDeals(Array.isArray(d) ? d : []);

        const c = contactsRes.status === 'fulfilled'
          ? (contactsRes.value?.total ?? contactsRes.value?.items?.length ?? 0)
          : 0;
        setTotalContacts(c);

        const a = activitiesRes.status === 'fulfilled'
          ? (activitiesRes.value?.items ?? activitiesRes.value ?? [])
          : [];
        setActivities(Array.isArray(a) ? a : []);
      } catch (err) {
        console.error('Failed to load analytics:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  /* ── derived metrics ─────────────────────────────────────────── */
  const closedWon = deals.filter((d) => d.stage === 'closed_won');
  const closedLost = deals.filter((d) => d.stage === 'closed_lost');
  const totalClosed = closedWon.length + closedLost.length;
  const winRate = totalClosed > 0 ? Math.round((closedWon.length / totalClosed) * 100) : 0;

  const activeDeals = deals.filter((d) => !['closed_won', 'closed_lost'].includes(d.stage));
  const avgDealSize = activeDeals.length > 0
    ? Math.round(activeDeals.reduce((sum, d) => sum + (d.value || 0), 0) / activeDeals.length)
    : 0;

  const pipelineTotal = activeDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  const wonTotal = closedWon.reduce((sum, d) => sum + (d.value || 0), 0);
  const coverage = wonTotal > 0 ? Math.round((pipelineTotal / wonTotal) * 10) / 10 : pipelineTotal > 0 ? 999 : 0;

  const heroCards = [
    { label: 'Win Rate',          value: `${winRate}%`,           accent: 'sage' },
    { label: 'Active Deals',      value: String(activeDeals.length), accent: 'blue' },
    { label: 'Avg Deal Size',     value: `$${avgDealSize >= 1000 ? `${(avgDealSize / 1000).toFixed(1)}k` : avgDealSize}`, accent: 'amber' },
    { label: 'Pipeline Coverage',  value: `${coverage}x`,         accent: 'purple' },
  ];

  /* ── pipeline breakdown data ─────────────────────────────────── */
  const stageGroups: Record<string, { count: number; value: number }> = {};
  deals.forEach((d) => {
    const stage = d.stage || 'unknown';
    if (!stageGroups[stage]) stageGroups[stage] = { count: 0, value: 0 };
    stageGroups[stage].count++;
    stageGroups[stage].value += d.value || 0;
  });
  const pipelineData = Object.entries(stageGroups).map(([name, data]) => ({
    name: name.replace('_', ' '),
    count: data.count,
    value: data.value,
    fill: stageColors[name] || '#90b9ab',
  }));

  /* ── activity trend data (group by date) ─────────────────────── */
  const dateMap: Record<string, number> = {};
  activities.forEach((a) => {
    if (a.created_at) {
      const date = new Date(a.created_at).toISOString().slice(0, 10);
      dateMap[date] = (dateMap[date] || 0) + 1;
    }
  });
  const activityTrend = Object.entries(dateMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-14)
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count,
    }));

  /* ── win/loss donut data ─────────────────────────────────────── */
  const donutData = [
    { name: 'Won',  value: closedWon.length,  color: '#22c55e' },
    { name: 'Lost', value: closedLost.length, color: '#ef4444' },
    { name: 'Open', value: activeDeals.length, color: '#3b82f6' },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Analytics" subtitle="Sales performance metrics" />
        <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
          <p className="text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <Header title="Analytics" subtitle="Sales performance metrics" />
      <div className="p-4 md:p-8 space-y-6">

        {/* ── Hero Metrics Row ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {heroCards.map((card) => {
            const a = accents[card.accent];
            return (
              <div
                key={card.label}
                className="rounded-xl p-5 transition-all duration-200"
                style={{ background: a.bg, border: `1px solid ${a.border}` }}
              >
                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                  {card.label}
                </p>
                <span className="font-display text-3xl" style={{ color: 'var(--text-primary)' }}>
                  {card.value}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Charts Row 1: Pipeline Breakdown + Activity Trend ────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Pipeline Breakdown — horizontal bar chart */}
          <Panel title="Pipeline Breakdown">
            {pipelineData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <p className="text-sm">No deal data available yet.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(180, pipelineData.length * 44)}>
                <BarChart layout="vertical" data={pipelineData} margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    stroke="var(--text-muted)"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    style={{ textTransform: 'capitalize' }}
                  />
                  <Tooltip
                    {...tooltipStyle}
                    formatter={(value: any) => [value, 'Deals']}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                    {pipelineData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* Activity Trend — line chart */}
          <Panel title="Activity Trend">
            {activityTrend.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <p className="text-sm">No activity data to chart yet.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={activityTrend} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#90b9ab"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#90b9ab', stroke: 'var(--bg-panel)', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        {/* ── Charts Row 2: Win Rate Donut + Revenue Summary ─────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Win Rate Donut */}
          <Panel title="Deal Outcomes">
            {donutData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <p className="text-sm">No closed deals to analyze.</p>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-8">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {donutData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{entry.name}</span>
                      <span className="font-display text-sm ml-2" style={{ color: 'var(--text-primary)' }}>
                        {entry.value}
                      </span>
                    </div>
                  ))}
                  {totalClosed > 0 && (
                    <div className="pt-2 mt-2" style={{ borderTop: '1px solid var(--border-primary)' }}>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Win Rate</span>
                      <p className="font-display text-2xl" style={{ color: '#22c55e' }}>{winRate}%</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Panel>

          {/* Revenue Summary */}
          <Panel title="Revenue Summary">
            <div className="space-y-4">
              {[
                { label: 'Pipeline Value', value: `$${pipelineTotal.toLocaleString()}`, color: 'var(--text-accent)' },
                { label: 'Won Revenue',    value: `$${wonTotal.toLocaleString()}`,     color: '#22c55e' },
                { label: 'Total Contacts', value: totalContacts.toLocaleString(),      color: 'var(--text-primary)' },
                { label: 'Total Activities', value: activities.length.toLocaleString(), color: 'var(--text-primary)' },
                { label: 'Avg Deal Size',  value: `$${avgDealSize.toLocaleString()}`,  color: 'var(--text-accent)' },
                { label: 'Win Rate',       value: `${winRate}%`,                       color: winRate >= 50 ? '#22c55e' : '#fbbf24' },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between py-2.5"
                  style={{ borderBottom: '1px solid var(--border-primary)' }}
                >
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{row.label}</span>
                  <span className="font-display text-sm" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

      </div>
    </div>
  );
}
